import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartEmpty,
  ChartTooltip,
  GRID,
  Legend,
  SERIES,
  axisTick,
  compactTick,
} from "./chart-bits";

type Props = { data: any[] | null };

const KEYS = [
  { key: "icbCollection", label: "ICB", color: SERIES[0] },
  { key: "debtSureCollectionViaPortal", label: "Portal", color: SERIES[1] },
  { key: "debtSureCollectionViaAgent", label: "Agents", color: SERIES[2] },
  { key: "debtSureCollectionViaRobot", label: "Robot", color: SERIES[3] },
];

/**
 * Daily collection movement, stacked by channel.
 *
 * Part-to-whole over time, so a stacked column is the right form. Mark specs: a 24px
 * cap so columns never fill their band, a 4px rounded cap on the topmost segment only
 * (rounding every segment would misstate the boundaries), and a 2px surface gap
 * between segments doing the separating instead of strokes.
 */
export default function MovementChart({ data }: Props) {
  const rows = data ?? [];
  const hasValues = rows.some((r) =>
    KEYS.some((k) => Number(r?.[k.key] ?? 0) > 0)
  );

  if (!rows.length || !hasValues) return <ChartEmpty />;

  return (
    <div className="flex flex-col h-full">
      <Legend items={KEYS} className="mb-3" />

      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
            <CartesianGrid stroke={GRID} strokeWidth={1} vertical={false} />
            <XAxis
              dataKey="name"
              tick={axisTick}
              tickLine={false}
              axisLine={{ stroke: GRID }}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              tick={axisTick}
              tickLine={false}
              axisLine={false}
              width={52}
              tickFormatter={compactTick}
            />
            <Tooltip
              cursor={{ fill: GRID }}
              content={({ active, payload, label }) =>
                active && payload?.length ? (
                  <ChartTooltip
                    title={label}
                    currency
                    rows={KEYS.map((k) => ({
                      label: k.label,
                      color: k.color,
                      value: payload.find((p) => p.dataKey === k.key)?.value,
                    }))}
                  />
                ) : null
              }
            />

            {KEYS.map((k, i) => (
              <Bar
                key={k.key}
                dataKey={k.key}
                stackId="collections"
                fill={k.color}
                maxBarSize={24}
                // The 2px separator is drawn in the SURFACE colour, so it reads as a
                // gap rather than as an outline around each segment.
                stroke="var(--color-surface)"
                strokeWidth={2}
                // Only the top segment gets the rounded data-end.
                radius={i === KEYS.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
